import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "src/environments/environment";
import { DroneResource } from "../models";
import { firstValueFrom } from "rxjs";
import { FileUploadService } from "./file-upload.service";

interface ApiResponse<T> {
    success: boolean;
    data?: T;
    error?: string;
  }

/** Response from POST /admin/file-uploads/drone-resource */
interface PresignResponse {
    uploadUrl: string;
    s3Key: string;
    cdnUrl: string;
}

/** Caller-supplied metadata for a new resource row. */
export interface DroneResourceUpload {
    resourceType: string;
    adminOnly: boolean;
    caption?: string;
}

@Injectable({
    providedIn: 'root'
})
export class DroneResourceService {
    private readonly dronesUrl = `${environment.apiUrl}/admin/drones`;

    constructor(
        private http: HttpClient,
        private fileUploadService: FileUploadService
    ){}

    async getResourcesByDroneId(
        droneId: string,
        resourceType?: string,
        adminOnly?: string
    ): Promise<DroneResource[] | null> {
        const params = new URLSearchParams();

        if(resourceType) {
            params.append('resourceType', resourceType);
        }

        if(adminOnly) {
            params.append('adminOnly', adminOnly);
        }

        const query = params.toString();
        const url = `${this.dronesUrl}/${droneId}/resources${query ? `?${query}` : ''}`;

        const response = await firstValueFrom(
            this.http.get<ApiResponse<DroneResource[]>>(url)
        );

        if (response.success && response.data) {
            return response.data;
        }

        return null;
    }

    /**
     * Attach a file to a drone. Three steps, kept together so a caller never
     * has to orchestrate them or handle a partial failure:
     *
     *   1. Ask the Hub to pre-sign a PUT — only the server holds the signing key
     *   2. PUT the bytes straight to S3 — keeps large files off the Hub
     *   3. Register the row, passing back the s3Key + cdnUrl from step 1
     *
     * Throws on validation failure or any step failing, so the caller can show
     * one error path. A failure at step 3 leaves an orphaned S3 object; that's
     * the same tradeoff the existing attachment flows accept.
     */
    async uploadResourceByDroneId(
        droneId: string,
        file: File,
        meta: DroneResourceUpload
    ): Promise<DroneResource> {
        const validationError = this.fileUploadService.validate(file);
        if (validationError) {
            throw new Error(validationError);
        }

        // 1 + 2 — pre-sign, then bytes to S3.
        const { s3Key, cdnUrl } = await this.presignAndUpload(droneId, file);

        // 3 — persist the metadata row.
        const registered = await firstValueFrom(
            this.http.post<ApiResponse<DroneResource>>(
                `${this.dronesUrl}/${droneId}/resources`,
                {
                    resourceType: meta.resourceType,
                    adminOnly: meta.adminOnly,
                    caption: meta.caption ?? null,
                    fileName: file.name,
                    fileSize: file.size,
                    fileType: file.type,
                    fileExtension: this.fileUploadService.getFileExtension(file.name),
                    s3Key,
                    cdnUrl,
                }
            )
        );

        if (!registered.success || !registered.data) {
            throw new Error(registered.error || 'Upload saved to storage but could not be recorded.');
        }

        return registered.data;
    }

    /**
     * Update a resource. Pass a file to replace the document as well —
     * that runs the same presign + PUT as an upload before the PATCH.
     */
    async updateResource(
        droneId: string,
        resourceId: string,
        meta: DroneResourceUpload,
        file?: File | null
    ): Promise<DroneResource> {
        const body: Record<string, any> = {
            resourceType: meta.resourceType,
            adminOnly: meta.adminOnly,
            caption: meta.caption ?? null,
        };

        if (file) {
            const validationError = this.fileUploadService.validate(file);
            if (validationError) {
                throw new Error(validationError);
            }

            const { s3Key, cdnUrl } = await this.presignAndUpload(droneId, file);

            body['fileName'] = file.name;
            body['fileSize'] = file.size;
            body['fileType'] = file.type;
            body['fileExtension'] = this.fileUploadService.getFileExtension(file.name);
            body['s3Key'] = s3Key;
            body['cdnUrl'] = cdnUrl;
        }

        const response = await firstValueFrom(
            this.http.patch<ApiResponse<DroneResource>>(
                `${this.dronesUrl}/${droneId}/resources/${resourceId}`,
                body
            )
        );

        if (!response.success || !response.data) {
            throw new Error(response.error || 'Could not save your changes.');
        }

        return response.data;
    }

    /**
     * Pre-sign and PUT the bytes. `false` on uploadToS3 because the Hub signs
     * drone resources private, so no x-amz-acl header must be sent.
     */
    private async presignAndUpload(
        droneId: string,
        file: File
    ): Promise<{ s3Key: string; cdnUrl: string }> {
        const presign = await firstValueFrom(
            this.http.post<ApiResponse<PresignResponse>>(
                `${this.dronesUrl}/${droneId}/resources/upload-url`,
                {
                    filename: file.name,
                    contentType: file.type || 'application/octet-stream',
                }
            )
        );

        if (!presign.success || !presign.data) {
            throw new Error(presign.error || 'Could not prepare the upload.');
        }

        await this.fileUploadService.uploadToS3(file, presign.data.uploadUrl, false);

        return { s3Key: presign.data.s3Key, cdnUrl: presign.data.cdnUrl };
    }

    /**
     * Mint a short-lived signed GET for a resource.
     *
     * Resources are stored PRIVATE, so the `cdnUrl` on the row is not directly
     * loadable — every view or download has to come through here. The URL
     * expires in an hour, so fetch it at click time rather than caching it.
     */
    async getResourceDownloadUrl(
        droneId: string,
        resourceId: string
    ): Promise<string> {
        const response = await firstValueFrom(
            this.http.get<ApiResponse<{ downloadUrl: string }>>(
                `${this.dronesUrl}/${droneId}/resources/${resourceId}/download-url`
            )
        );

        if (!response.success || !response.data?.downloadUrl) {
            throw new Error(response.error || 'Could not open this resource.');
        }

        return response.data.downloadUrl;
    }

    /**
     * Soft-delete a resource. The S3 object is intentionally left in place.
     */
    async deleteResource(
        droneId: string,
        resourceId: string
    ): Promise<void> {
        const response = await firstValueFrom(
            this.http.delete<ApiResponse<never>>(
                `${this.dronesUrl}/${droneId}/resources/${resourceId}`
            )
        );

        if (!response.success) {
            throw new Error(response.error || 'Could not delete this resource.');
        }
    }
}
